import {
  Box,
  Grid,
  IconButton,
  ScaleFade,
  useMediaQuery
} from '@chakra-ui/react'
import { FC, useMemo, useRef, useState } from 'react'
import { FaAngleRight, FaAngleLeft } from 'react-icons/fa'
import Slider from 'react-slick'

import useSessions from '@hook/shared/use-sessions'

import {
  overviewGeneralStockParser,
  overviewGeneralStockParserFooter,
  overviewProductParser,
  overviewPurchaseOrdersApprovalParser,
  overviewPurchaseOrdersApprovalParserFooter,
  overviewStockParser
} from '@helper/grid'

import overviewGeneralStockTableHeaders from '@base/variables/headers/overview/general-stock-table-headers'
import overviewProductTableHeaders from '@base/variables/headers/overview/product-table-headers'
import overviewPurchaseOrdersApprovalTableHeaders from '@base/variables/headers/overview/purchase-orders-approval-table-headers'
import overviewStockTableHeaders from '@base/variables/headers/overview/stock-table-headers'

import ActualSaleVersusBudgetedSalesCharts from './Charts/ActualSaleVersusBudgetedSale'
import PurchaseOrdersApprovalCharts from './Charts/PurchaseOrdersApproval'
import RuptureCharts from './Charts/Rupture'
import OverviewTable from './Table'

interface IOverviewCarouselProps {
  empresas: Array<number>
}

const OverviewCarousel: FC<IOverviewCarouselProps> = ({ empresas }) => {
  const { hasSessions } = useSessions()
  const sliderRef = useRef<Slider>(null)

  const [position, setPosition] = useState(0)

  const [isLargerThanHD, isDisplayingInBrowser] = useMediaQuery([
    '(min-width: 780px)',
    '(display-mode: browser)'
  ])

  const settings = useMemo(
    () => ({
      dots: false,
      arrows: false,
      infinite: false,
      speed: 300,
      slidesToShow: 1,
      slidesToScroll: 1,
      afterChange: setPosition
    }),
    []
  )

  const showRuptureSlide = !isLargerThanHD && isDisplayingInBrowser

  // A quantidade de slides define até onde a seta "Posterior" aparece
  const slides = [
    <Box key="sales-rupture" maxW="100%" px={3}>
      <Grid templateColumns={['', '', '1fr 1fr']} gap="16px" height="470px">
        <ActualSaleVersusBudgetedSalesCharts empresas={empresas} />

        <Box height="100%" display={['none', 'none', 'block']}>
          <RuptureCharts empresas={empresas} />
        </Box>
      </Grid>
    </Box>,
    showRuptureSlide && (
      <Box key="rupture-mobile" px={3}>
        <RuptureCharts empresas={empresas} />
      </Box>
    ),
    <Box key="purchase-orders-approval-chart" maxW="100%" px={3}>
      <PurchaseOrdersApprovalCharts empresas={empresas} />
    </Box>,
    <OverviewTable
      key="storage-by-status"
      id="storage-by-status"
      title="Estoque Geral p/ Status"
      endpoint="Estoques/Geral/Status"
      empresasIds={empresas}
      entity="vendas"
      headers={overviewGeneralStockTableHeaders}
      parser={overviewGeneralStockParser}
      parserFooter={overviewGeneralStockParserFooter}
      isActionFooter={true}
    />,
    <OverviewTable
      key="product-status-order"
      id="product-status-order"
      title="Produto Status Encomenda"
      subtitle="TOP 15"
      endpoint="Produtos/StatusEncomenda"
      entity="encomenda"
      empresasIds={empresas}
      headers={overviewProductTableHeaders}
      parser={overviewProductParser}
      isExpandable
    />,
    <OverviewTable
      key="product-status-slsf"
      id="product-status-slsf"
      title="Produtos Status SL e SF"
      subtitle="TOP 15"
      endpoint="Produtos/StatusSLSF"
      entity="slsf"
      empresasIds={empresas}
      headers={overviewProductTableHeaders}
      parser={overviewProductParser}
      isExpandable
    />,
    <OverviewTable
      key="product-without-sales"
      id="product-without-sales"
      title="Produtos Sem Vendas"
      subtitle="TOP 15 dos últimos 6 meses"
      endpoint="Produtos/SemVendas"
      entity="semVendas"
      empresasIds={empresas}
      headers={overviewProductTableHeaders}
      parser={overviewProductParser}
      isExpandable
    />,
    <OverviewTable
      key="excess-stock-products"
      id="excess-stock-products"
      title="Excesso de Produtos em Estoque"
      subtitle="TOP 15"
      endpoint="Produtos/EstoqueEmExcesso"
      entity="estoque"
      empresasIds={empresas}
      headers={overviewProductTableHeaders}
      parser={overviewProductParser}
      isExpandable
    />,
    <OverviewTable
      key="stock-in-suppliers-portfolio"
      id="stock-in-suppliers-portfolio"
      title="Estoque em Carteira Fornecedor"
      subtitle="TOP 15"
      endpoint="Estoques/CarteiraFornecedor"
      entity="carteiraFornecedor"
      empresasIds={empresas}
      headers={overviewStockTableHeaders}
      parser={overviewStockParser}
    />,
    <OverviewTable
      key="purchase-orders-approval"
      id="purchase-orders-approval"
      title="Aprovação de Pedidos de Compra"
      subtitle="Status Total"
      entity="pedidosAprovacao"
      empresasIds={empresas}
      headers={overviewPurchaseOrdersApprovalTableHeaders}
      parser={overviewPurchaseOrdersApprovalParser}
      parserFooter={overviewPurchaseOrdersApprovalParserFooter}
      isActionFooter={true}
    />
  ].filter(Boolean)

  const handleNext = () => sliderRef.current?.slickNext()

  const handlePrev = () => sliderRef.current?.slickPrev()

  return (
    <Box
      pos="relative"
      pb={hasSessions ? 4 : 0}
      w={'calc(100% + 1.5rem)'}
      ml={-3}
      transition="all ease .3s"
    >
      <Slider ref={sliderRef} {...settings}>
        {slides}
      </Slider>

      <Box pos="absolute" top="50%" left={'-4px'} transform="translateY(-50%)">
        <ScaleFade initialScale={0.9} in={position > 0} unmountOnExit>
          <IconButton
            size="sm"
            variant="unstyled"
            colorScheme="yellow"
            aria-label="Anterior"
            icon={<FaAngleLeft />}
            fontSize={24}
            display="flex"
            onClick={handlePrev}
            transition="all ease .3s"
            _hover={{
              fontSize: 20,
              display: 'flex',
              justifyContent: 'center',
              background: '#fad954'
            }}
          />
        </ScaleFade>
      </Box>
      <Box pos="absolute" top="50%" right={'-4px'} transform="translateY(-50%)">
        <ScaleFade
          initialScale={0.9}
          in={position < slides.length - 1}
          unmountOnExit
        >
          <IconButton
            size="sm"
            variant="unstyled"
            colorScheme="yellow"
            aria-label="Posterior"
            icon={<FaAngleRight />}
            fontSize={24}
            display="flex"
            onClick={handleNext}
            transition="all ease .3s"
            _hover={{
              fontSize: 20,
              display: 'flex',
              justifyContent: 'center',
              background: '#fad954'
            }}
          />
        </ScaleFade>
      </Box>
    </Box>
  )
}

export default OverviewCarousel
